/**
 * ================================================================
 * AGENCY SERVICE - Comprehensive Agency Management
 * Handles all agency dashboard operations
 * ================================================================
 */

import { supabase } from '../lib/supabase';

export interface AgencyDashboardStats {
  total_brands: number;
  active_campaigns: number;
  pending_approvals: number;
  total_influencers_used: number;
  total_spend: number;
  total_budget: number;
  spend_percentage: number;
  upcoming_deadlines: Array<{ campaign_id: string; title: string; end_date: string }>;
  top_performers: Array<{ creator_id: string; name: string; performance: number }>;
  unread_messages: number;
  recent_notifications: Array<{ type: string; title: string; timestamp: string }>;
  total_revenue: number;
  total_commission: number;
  avg_campaign_performance: number;
  brands_change: number;
  campaigns_change: number;
  spend_change: number;
}

export interface BrandRelationship {
  id: string;
  brandId: string;
  brandName: string;
  brandLogo?: string;
  relationshipStatus: string;
  accessLevel: string;
  campaignCount: number;
  totalBudget: number;
  createdAt: string;
  industry?: string;
  contactPerson?: string;
}

export interface AgencyCampaign {
  id: string;
  title: string;
  description: string;
  brandId: string;
  brandName: string;
  status: string;
  budget: number;
  spent: number;
  targetReach: number;
  currentReach: number;
  startDate: string;
  endDate: string;
  applicationsCount: number;
  approvedCreators: number;
}

export interface CreatorNetwork {
  id: string;
  creatorId: string;
  creatorName: string;
  creatorAvatar?: string;
  category: string;
  followers: number;
  performanceRating: number;
  collaborationCount: number;
  networkStatus: string;
  engagementRate: number;
}

export interface AgencyPayment {
  id: string;
  amount: number;
  commission_amount: number;
  payment_status: string;
  payment_date: string;
  brand?: { full_name: string; company_name: string };
  campaign?: { title: string };
  invoice_number: string;
  transaction_id: string;
}

export interface AgencySettings {
  id: string;
  agency_id: string;
  agency_name: string;
  agency_logo_url?: string;
  primary_contact_name?: string;
  primary_contact_email?: string;
  primary_email?: string;
  primary_contact_phone?: string;
  business_address?: Record<string, unknown>;
  business_phone?: string;
  business_email?: string;
  business_website?: string;
  social_handles?: Record<string, string>;
  about_agency?: string;
  services_offered?: string[];
  industry_focus?: string[];
  specializations?: string[];
  team_size?: number | string;
  years_in_business?: number;
  onboarding_completed?: boolean;
  // GST & Taxation fields
  gstin?: string;
  legal_business_name?: string;
  business_pan?: string;
  gst_registration_date?: string;
  enable_gst_invoicing?: boolean;
  auto_calculate_gst?: boolean;
  tds_applicable?: boolean;
}

export const agencyService = {
  /**
   * Get comprehensive dashboard statistics
   */
  async getAgencyStats(agencyUserId: string): Promise<AgencyDashboardStats | null> {
    try {
      // Try RPC first
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('get_agency_dashboard_metrics', { agency_user_id: agencyUserId });

      if (!rpcError && rpcData) {
        return rpcData as AgencyDashboardStats;
      }

      // Fallback to aggregating data manually
      const [brandsResult, campaignsResult, applicationsResult] = await Promise.all([
        supabase
          .from('agency_brand_relationships')
          .select('id, brand_id', { count: 'exact' })
          .eq('agency_id', agencyUserId)
          .eq('relationship_status', 'active'),
        supabase
          .from('campaigns')
          .select('id, budget, status', { count: 'exact' })
          .eq('brand_id', agencyUserId),
        supabase
          .from('campaign_applications')
          .select('id, status', { count: 'exact' })
          .eq('status', 'pending')
      ]);

      const totalBrands = brandsResult.count || 0;
      const campaigns = campaignsResult.data || [];
      const activeCampaigns = campaigns.filter(c => c.status === 'active').length;
      const totalBudget = campaigns.reduce((sum, c) => sum + (c.budget || 0), 0);
      const pendingApprovals = applicationsResult.count || 0;

      return {
        total_brands: totalBrands,
        active_campaigns: activeCampaigns,
        pending_approvals: pendingApprovals,
        total_influencers_used: 0,
        total_spend: 0,
        total_budget: totalBudget,
        spend_percentage: 0,
        upcoming_deadlines: [],
        top_performers: [],
        unread_messages: 0,
        recent_notifications: [],
        total_revenue: 0,
        total_commission: 0,
        avg_campaign_performance: 0,
        brands_change: 0,
        campaigns_change: 0,
        spend_change: 0,
      };
    } catch (error) {
      console.error('Error getting agency stats:', error);
      return null;
    }
  },

  /**
   * Get aggregated metrics for the dashboard
   */
  async getDashboardMetrics(agencyUserId: string) {
    try {
      const stats = await this.getAgencyStats(agencyUserId);
      return {
        totalBrands: stats?.total_brands || 0,
        totalCampaigns: stats?.active_campaigns || 0,
        totalCreators: stats?.total_influencers_used || 0,
        activeCampaigns: stats?.active_campaigns || 0,
        totalReach: 0,
        totalBudget: stats?.total_budget || 0,
        averageRoi: 0,
      };
    } catch (error) {
      console.error('Error getting dashboard metrics:', error);
      return null;
    }
  },

  /**
   * Get all brands managed by the agency
   */
  async getManagedBrands(agencyUserId: string): Promise<BrandRelationship[]> {
    try {
      // First get the relationships
      const { data: relationships, error: relError } = await supabase
        .from('agency_brand_relationships')
        .select('id, brand_id, relationship_status, access_level, created_at, contact_person, notes')
        .eq('agency_id', agencyUserId);

      if (relError) {
        console.warn('Agency brand relationships table may not exist:', relError.message);
        return [];
      }

      if (!relationships || relationships.length === 0) {
        return [];
      }

      // Get brand IDs
      const brandIds = relationships.map(r => r.brand_id).filter(Boolean);
      
      if (brandIds.length === 0) {
        return [];
      }

      // Fetch brand profiles separately
      const { data: brands, error: brandError } = await supabase
        .from('user_profiles')
        .select('id, full_name, avatar_url, company_name')
        .in('id', brandIds);

      if (brandError) {
        console.warn('Error fetching brand profiles:', brandError.message);
      }

      // Create a map for quick lookup
      const brandMap = new Map<string, Record<string, unknown>>();
      (brands || []).forEach((brand: Record<string, unknown>) => {
        brandMap.set(String(brand.id), brand);
      });

      // Get campaign counts for each brand
      const { data: campaignCounts } = await supabase
        .from('campaigns')
        .select('brand_id, budget')
        .in('brand_id', brandIds);

      const campaignMap = new Map<string, { count: number; budget: number }>();
      (campaignCounts || []).forEach((c: Record<string, unknown>) => {
        const brandId = String(c.brand_id);
        const current = campaignMap.get(brandId) || { count: 0, budget: 0 };
        campaignMap.set(brandId, {
          count: current.count + 1,
          budget: current.budget + Number(c.budget || 0),
        });
      });

      // Fetch pending invitations
      const { data: invitations } = await supabase
        .from('brand_invitations')
        .select('id, brand_name, contact_person, status, created_at')
        .eq('agency_id', agencyUserId)
        .eq('status', 'pending');

      const mappedRelationships: BrandRelationship[] = relationships.map((rel: any) => {
        const brand = brandMap.get(rel.brand_id);
        const stats = campaignMap.get(rel.brand_id) || { count: 0, budget: 0 };
        
        return {
          id: rel.id,
          brandId: rel.brand_id,
          brandName: String(brand?.company_name || brand?.full_name || 'Unknown Brand'),
          brandLogo: brand?.avatar_url ? String(brand.avatar_url) : undefined,
          relationshipStatus: rel.relationship_status,
          accessLevel: rel.access_level,
          campaignCount: stats.count,
          totalBudget: stats.budget,
          createdAt: rel.created_at,
          industry: undefined, // TODO: Add to query if available in user_profiles metadata
          contactPerson: rel.contact_person,
        };
      });

      // Map invitations to BrandRelationship format
      const mappedInvitations: BrandRelationship[] = (invitations || []).map((inv: any) => ({
        id: inv.id,
        brandId: `invite_${inv.id}`, // Temporary ID for UI
        brandName: inv.brand_name,
        brandLogo: undefined,
        relationshipStatus: 'invited', // Custom status for UI
        accessLevel: 'pending',
        campaignCount: 0,
        totalBudget: 0,
        createdAt: inv.created_at,
        industry: undefined,
        contactPerson: inv.contact_person,
      }));

      return [...mappedRelationships, ...mappedInvitations];
    } catch (error) {
      console.error('Error getting managed brands:', error);
      return [];
    }
  },

  /**
   * Add a new brand relationship
   */
  async addBrandRelationship(agencyUserId: string, brandId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('agency_brand_relationships')
        .insert({
          agency_id: agencyUserId,
          brand_id: brandId,
          relationship_status: 'pending',
          access_level: 'limited',
        });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error adding brand relationship:', error);
      return false;
    }
  },

  /**
   * Remove a brand relationship
   */
  async removeBrandRelationship(agencyUserId: string, brandId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('agency_brand_relationships')
        .delete()
        .eq('agency_id', agencyUserId)
        .eq('brand_id', brandId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error removing brand relationship:', error);
      return false;
    }
  },

  /**
   * Update brand relationship settings
   */
  async updateBrandRelationship(
    agencyUserId: string, 
    brandId: string, 
    updates: {
      access_level?: 'view_only' | 'manage' | 'full_access';
      relationship_status?: 'active' | 'pending' | 'inactive';
      contact_person?: string;
      contact_email?: string;
      notes?: string;
    }
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('agency_brand_relationships')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('agency_id', agencyUserId)
        .eq('brand_id', brandId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating brand relationship:', error);
      return false;
    }
  },

  /**
   * Invite brand via email
   */
  async inviteBrandByEmail(
    agencyUserId: string,
    inviteData: {
      email: string;
      brandName: string;
      contactPerson?: string;
      message?: string;
    }
  ): Promise<{ success: boolean; inviteId?: string; error?: string }> {
    try {
      // Check if email already exists in user_profiles (not auth.users which requires admin access)
      const { data: existingUser, error: checkError } = await supabase
        .from('user_profiles')
        .select('id, role')
        .eq('email', inviteData.email)
        .maybeSingle();

      // If there's an error checking (table permission issue), proceed anyway
      if (checkError) {
        console.warn('Could not check existing user:', checkError);
      }

      if (existingUser) {
        return { success: false, error: 'A user with this email already exists. Try searching for them instead.' };
      }

      // Check if there's already a pending invitation for this email
      const { data: existingInvite } = await supabase
        .from('brand_invitations')
        .select('id, status')
        .eq('email', inviteData.email)
        .eq('agency_id', agencyUserId)
        .eq('status', 'pending')
        .maybeSingle();

      if (existingInvite) {
        return { success: false, error: 'An invitation has already been sent to this email' };
      }

      // Create brand invitation record
      const { data, error } = await supabase
        .from('brand_invitations')
        .insert({
          agency_id: agencyUserId,
          email: inviteData.email,
          brand_name: inviteData.brandName,
          contact_person: inviteData.contactPerson,
          invitation_message: inviteData.message,
          status: 'pending',
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating invitation:', error);
        // If table doesn't exist, provide helpful message
        if (error.code === '42P01') {
          return { success: false, error: 'Invitation system not set up. Please run the database migration.' };
        }
        throw error;
      }

      // Send invitation email via edge function (optional - won't fail if edge function not deployed)
      try {
        await supabase.functions.invoke('send-brand-invitation', { 
          body: { inviteId: data.id } 
        });
      } catch (emailError) {
        // Log but don't fail - invitation is still created
        console.warn('Could not send invitation email (edge function may not be deployed):', emailError);
      }

      return { success: true, inviteId: data?.id };
    } catch (error) {
      console.error('Error inviting brand:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send invitation';
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Create a new brand account directly
   */
  async createBrandAccount(
    agencyUserId: string,
    brandData: {
      email: string;
      fullName: string;
      companyName: string;
      industry?: string;
      phone?: string;
      password: string;
    }
  ): Promise<{ success: boolean; brandId?: string; error?: string }> {
    try {
      // First check if email already exists in user_profiles
      const { data: existingProfile } = await supabase
        .from('user_profiles')
        .select('id, email, role')
        .eq('email', brandData.email.toLowerCase())
        .maybeSingle();

      if (existingProfile) {
        // User already exists - check if already linked to this agency
        const { data: existingRelation } = await supabase
          .from('agency_brand_relationships')
          .select('id')
          .eq('agency_id', agencyUserId)
          .eq('brand_id', existingProfile.id)
          .maybeSingle();

        if (existingRelation) {
          return { success: false, error: 'This brand is already linked to your agency' };
        }

        // If user exists but not linked, offer to link them
        return { 
          success: false, 
          error: 'An account with this email already exists. Use the "Search" tab to find and add this brand.',
          brandId: existingProfile.id
        };
      }

      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: brandData.email,
        password: brandData.password,
        options: {
          data: {
            role: 'brand',
            full_name: brandData.fullName,
          },
        },
      });

      if (authError) {
        // Handle specific auth errors
        if (authError.message.includes('already registered')) {
          return { success: false, error: 'An account with this email already exists' };
        }
        throw authError;
      }
      if (!authData.user) throw new Error('Failed to create user');

      // Check if user was created or already existed (Supabase returns existing user on duplicate)
      const isNewUser = authData.user.created_at && 
        new Date(authData.user.created_at).getTime() > Date.now() - 10000; // Created in last 10 seconds

      // Create user profile only for new users
      if (isNewUser) {
        const { error: profileError } = await supabase
          .from('user_profiles')
          .insert({
            id: authData.user.id,
            email: brandData.email.toLowerCase(),
            full_name: brandData.fullName,
            company_name: brandData.companyName,
            phone: brandData.phone,
            role: 'brand',
            onboarding_completed: false,
          });

        if (profileError) {
          // If profile already exists, just continue to create relationship
          if (!profileError.message.includes('duplicate key')) {
            throw profileError;
          }
        }
      }

      // Create agency-brand relationship
      const { error: relError } = await supabase
        .from('agency_brand_relationships')
        .insert({
          agency_id: agencyUserId,
          brand_id: authData.user.id,
          relationship_status: 'active',
          access_level: 'full_access',
        });

      if (relError) {
        if (relError.message.includes('duplicate key')) {
          return { success: false, error: 'This brand is already linked to your agency' };
        }
        throw relError;
      }

      return { success: true, brandId: authData.user.id };
    } catch (error) {
      console.error('Error creating brand account:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create brand account';
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Get brand analytics and insights
   */
  async getBrandAnalytics(agencyUserId: string, brandId: string): Promise<{
    totalCampaigns: number;
    activeCampaigns: number;
    completedCampaigns: number;
    totalSpend: number;
    totalBudget: number;
    avgROI: number;
    successRate: number;
    totalReach: number;
    totalEngagement: number;
    creatorsUsed: number;
    monthlySpend: Array<{ month: string; amount: number }>;
    topPerformingCampaigns: Array<{ id: string; title: string; roi: number }>;
  }> {
    try {
      // Get all campaigns for this brand
      const { data: campaigns } = await supabase
        .from('campaigns')
        .select('id, title, status, budget, spent_amount, target_reach, current_reach, start_date, end_date')
        .eq('brand_id', brandId);

      const campaignList = campaigns || [];
      const totalCampaigns = campaignList.length;
      const activeCampaigns = campaignList.filter(c => c.status === 'active').length;
      const completedCampaigns = campaignList.filter(c => c.status === 'completed').length;
      const totalSpend = campaignList.reduce((sum, c) => sum + (c.spent_amount || 0), 0);
      const totalBudget = campaignList.reduce((sum, c) => sum + (c.budget || 0), 0);
      const totalReach = campaignList.reduce((sum, c) => sum + (c.current_reach || 0), 0);

      // Calculate ROI (simplified: reach per rupee spent)
      const avgROI = totalSpend > 0 ? (totalReach / totalSpend) * 100 : 0;
      
      // Success rate based on completed campaigns that met targets
      const successfulCampaigns = campaignList.filter(
        c => c.status === 'completed' && c.current_reach >= c.target_reach
      ).length;
      const successRate = completedCampaigns > 0 ? (successfulCampaigns / completedCampaigns) * 100 : 0;

      // Get engagement data
      const campaignIds = campaignList.map(c => c.id);
      const { data: posts } = await supabase
        .from('posts')
        .select('engagement_count')
        .in('campaign_id', campaignIds);
      
      const totalEngagement = (posts || []).reduce((sum, p) => sum + (p.engagement_count || 0), 0);

      // Get unique creators used
      const { data: applications } = await supabase
        .from('campaign_applications')
        .select('creator_id')
        .in('campaign_id', campaignIds)
        .eq('status', 'approved');
      
      const creatorsUsed = new Set((applications || []).map(a => a.creator_id)).size;

      // Monthly spend (last 6 months)
      const monthlySpend: Array<{ month: string; amount: number }> = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const month = date.toLocaleString('default', { month: 'short', year: '2-digit' });
        const monthCampaigns = campaignList.filter(c => {
          const startDate = new Date(c.start_date);
          return startDate.getMonth() === date.getMonth() && startDate.getFullYear() === date.getFullYear();
        });
        const amount = monthCampaigns.reduce((sum, c) => sum + (c.spent_amount || 0), 0);
        monthlySpend.push({ month, amount });
      }

      // Top performing campaigns by ROI
      const topPerformingCampaigns = campaignList
        .filter(c => c.spent_amount && c.spent_amount > 0)
        .map(c => ({
          id: c.id,
          title: c.title,
          roi: ((c.current_reach || 0) / c.spent_amount) * 100,
        }))
        .sort((a, b) => b.roi - a.roi)
        .slice(0, 5);

      return {
        totalCampaigns,
        activeCampaigns,
        completedCampaigns,
        totalSpend,
        totalBudget,
        avgROI,
        successRate,
        totalReach,
        totalEngagement,
        creatorsUsed,
        monthlySpend,
        topPerformingCampaigns,
      };
    } catch (error) {
      console.error('Error getting brand analytics:', error);
      return {
        totalCampaigns: 0,
        activeCampaigns: 0,
        completedCampaigns: 0,
        totalSpend: 0,
        totalBudget: 0,
        avgROI: 0,
        successRate: 0,
        totalReach: 0,
        totalEngagement: 0,
        creatorsUsed: 0,
        monthlySpend: [],
        topPerformingCampaigns: [],
      };
    }
  },

  /**
   * Add activity note for a brand
   */
  async addBrandNote(
    agencyUserId: string,
    brandId: string,
    note: string
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('agency_brand_notes')
        .insert({
          agency_id: agencyUserId,
          brand_id: brandId,
          note,
        });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error adding brand note:', error);
      return false;
    }
  },

  /**
   * Get brand activity notes
   */
  async getBrandNotes(
    agencyUserId: string,
    brandId: string
  ): Promise<Array<{ id: string; note: string; createdAt: string }>> {
    try {
      const { data, error } = await supabase
        .from('agency_brand_notes')
        .select('id, note, created_at')
        .eq('agency_id', agencyUserId)
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(n => ({
        id: n.id,
        note: n.note,
        createdAt: n.created_at,
      }));
    } catch (error) {
      console.error('Error getting brand notes:', error);
      return [];
    }
  },

  /**
   * Get brand onboarding status
   */
  async getBrandOnboardingStatus(brandId: string): Promise<{
    profileCompleted: boolean;
    logoUploaded: boolean;
    firstCampaignCreated: boolean;
    paymentMethodAdded: boolean;
    contractSigned: boolean;
    completionPercentage: number;
  }> {
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('avatar_url, company_name, industry, onboarding_completed')
        .eq('id', brandId)
        .single();

      const { data: campaigns } = await supabase
        .from('campaigns')
        .select('id')
        .eq('brand_id', brandId)
        .limit(1);

      const { data: contracts } = await supabase
        .from('brand_contracts')
        .select('id, signed_at')
        .eq('brand_id', brandId)
        .limit(1);

      const profileCompleted = !!(profile?.company_name && profile?.industry);
      const logoUploaded = !!profile?.avatar_url;
      const firstCampaignCreated = (campaigns?.length || 0) > 0;
      
      // Check payment methods table
      let paymentMethodAdded = false;
      try {
        const { count } = await supabase
          .from('agency_payment_methods')
          .select('*', { count: 'exact', head: true })
          .eq('agency_id', brandId);
        paymentMethodAdded = (count || 0) > 0;
      } catch (e) {
        console.warn('Failed to check payment methods', e);
      }

      const contractSigned = !!(contracts?.[0]?.signed_at);

      const steps = [profileCompleted, logoUploaded, firstCampaignCreated, paymentMethodAdded, contractSigned];
      const completionPercentage = (steps.filter(Boolean).length / steps.length) * 100;

      return {
        profileCompleted,
        logoUploaded,
        firstCampaignCreated,
        paymentMethodAdded,
        contractSigned,
        completionPercentage,
      };
    } catch (error) {
      console.error('Error getting onboarding status:', error);
      return {
        profileCompleted: false,
        logoUploaded: false,
        firstCampaignCreated: false,
        paymentMethodAdded: false,
        contractSigned: false,
        completionPercentage: 0,
      };
    }
  },

  /**
   * Upload contract for a brand
   */
  async uploadBrandContract(
    agencyUserId: string,
    brandId: string,
    file: File
  ): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      const fileName = `contracts/${agencyUserId}/${brandId}/${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('documents')
        .getPublicUrl(fileName);

      // Save contract record
      const { error: insertError } = await supabase
        .from('brand_contracts')
        .insert({
          agency_id: agencyUserId,
          brand_id: brandId,
          contract_url: urlData.publicUrl,
          file_name: file.name,
        });

      if (insertError) throw insertError;

      return { success: true, url: urlData.publicUrl };
    } catch (error) {
      console.error('Error uploading contract:', error);
      return { success: false, error: 'Failed to upload contract' };
    }
  },

  /**
   * Export brands list to CSV
   */
  async exportBrandsList(agencyUserId: string): Promise<string> {
    try {
      const brands = await this.getManagedBrands(agencyUserId);
      
      const headers = ['Brand Name', 'Industry', 'Status', 'Campaigns', 'Total Budget', 'Access Level', 'Since'];
      const rows = brands.map(b => [
        b.brandName,
        b.industry || '',
        b.relationshipStatus,
        b.campaignCount.toString(),
        b.totalBudget.toString(),
        b.accessLevel,
        new Date(b.createdAt).toLocaleDateString(),
      ]);

      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      return csv;
    } catch (error) {
      console.error('Error exporting brands:', error);
      return '';
    }
  },

  /**
   * Get all campaigns managed by the agency
   */
  async getAgencyCampaigns(agencyUserId: string): Promise<AgencyCampaign[]> {
    try {
      // First get agency's brand relationships
      const { data: brandRels } = await supabase
        .from('agency_brand_relationships')
        .select('brand_id')
        .eq('agency_id', agencyUserId)
        .eq('relationship_status', 'active');

      const brandIds = brandRels?.map(r => r.brand_id) || [];
      
      // Add agency's own campaigns
      brandIds.push(agencyUserId);

      const { data, error } = await supabase
        .from('campaigns')
        .select(`
          id,
          title,
          description,
          brand_id,
          status,
          budget,
          spent_amount,
          target_reach,
          current_reach,
          start_date,
          end_date,
          brand:user_profiles!campaigns_brand_id_fkey(
            full_name,
            company_name
          )
        `)
        .in('brand_id', brandIds)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get application counts for each campaign
      const campaignIds = data?.map(c => c.id) || [];
      const { data: applicationCounts } = await supabase
        .from('campaign_applications')
        .select('campaign_id, status')
        .in('campaign_id', campaignIds);

      const countMap = new Map<string, { total: number; approved: number }>();
      (applicationCounts || []).forEach((app: { campaign_id: string; status: string }) => {
        if (!countMap.has(app.campaign_id)) {
          countMap.set(app.campaign_id, { total: 0, approved: 0 });
        }
        const counts = countMap.get(app.campaign_id);
        if (counts) {
          counts.total++;
          if (app.status === 'approved') {
            counts.approved++;
          }
        }
      });

      return (data || []).map((campaign: Record<string, unknown>) => {
        const brand = campaign.brand as Record<string, unknown> | null;
        const counts = countMap.get(String(campaign.id)) || { total: 0, approved: 0 };
        return {
          id: String(campaign.id || ''),
          title: String(campaign.title || ''),
          description: String(campaign.description || ''),
          brandId: String(campaign.brand_id || ''),
          brandName: String(brand?.full_name || brand?.company_name || 'Unknown'),
          status: String(campaign.status || 'draft'),
          budget: Number(campaign.budget || 0),
          spent: Number(campaign.spent_amount || 0),
          targetReach: Number(campaign.target_reach || 0),
          currentReach: Number(campaign.current_reach || 0),
          startDate: String(campaign.start_date || ''),
          endDate: String(campaign.end_date || ''),
          applicationsCount: counts.total,
          approvedCreators: counts.approved,
        };
      });
    } catch (error) {
      console.error('Error getting agency campaigns:', error);
      return [];
    }
  },

  /**
   * Get the agency's creator network
   */
  async getCreatorNetwork(agencyUserId: string): Promise<CreatorNetwork[]> {
    try {
      const { data, error } = await supabase
        .from('agency_creator_network')
        .select(`
          id,
          creator_id,
          network_status,
          collaboration_count,
          performance_rating,
          creator:user_profiles!agency_creator_network_creator_id_fkey(
            id,
            full_name,
            avatar_url,
            category,
            follower_count,
            engagement_rate
          )
        `)
        .eq('agency_id', agencyUserId);

      if (error) {
        // Fallback if table doesn't exist
        console.warn('Agency creator network table may not exist:', error.message);
        return [];
      }

      return (data || []).map((item: Record<string, unknown>) => {
        const creator = item.creator as Record<string, unknown> | null;
        return {
          id: String(item.id || ''),
          creatorId: String(item.creator_id || ''),
          creatorName: String(creator?.full_name || 'Unknown Creator'),
          creatorAvatar: creator?.avatar_url as string | undefined,
          category: String(creator?.category || 'General'),
          followers: Number(creator?.follower_count || 0),
          performanceRating: Number(item.performance_rating || 0),
          collaborationCount: Number(item.collaboration_count || 0),
          networkStatus: String(item.network_status || 'active'),
          engagementRate: Number(creator?.engagement_rate || 0),
        };
      });
    } catch (error) {
      console.error('Error getting creator network:', error);
      return [];
    }
  },

  /**
   * Add a creator to the agency network
   */
  async addCreatorToNetwork(agencyUserId: string, creatorId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('agency_creator_network')
        .insert({
          agency_id: agencyUserId,
          creator_id: creatorId,
          network_status: 'active',
          collaboration_count: 0,
          performance_rating: 0,
        });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error adding creator to network:', error);
      return false;
    }
  },

  /**
   * Remove a creator from the network
   */
  async removeCreatorFromNetwork(agencyUserId: string, creatorId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('agency_creator_network')
        .delete()
        .eq('agency_id', agencyUserId)
        .eq('creator_id', creatorId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error removing creator from network:', error);
      return false;
    }
  },

  /**
   * Get payment history and transactions
   */
  async getPayments(agencyUserId: string): Promise<AgencyPayment[]> {
    try {
      const { data, error } = await supabase
        .from('agency_payments')
        .select(`
          id,
          amount,
          commission_amount,
          payment_status,
          payment_date,
          invoice_number,
          transaction_id,
          brand:user_profiles!agency_payments_brand_id_fkey(
            full_name,
            company_name
          ),
          campaign:campaigns!agency_payments_campaign_id_fkey(
            title
          )
        `)
        .eq('agency_id', agencyUserId)
        .order('payment_date', { ascending: false });

      if (error) {
        console.warn('Agency payments table may not exist:', error.message);
        return [];
      }

      return (data || []).map((payment: Record<string, unknown>) => {
        const brand = payment.brand as Record<string, unknown> | null;
        const campaign = payment.campaign as Record<string, unknown> | null;
        return {
          id: String(payment.id || ''),
          amount: Number(payment.amount || 0),
          commission_amount: Number(payment.commission_amount || 0),
          payment_status: String(payment.payment_status || 'pending'),
          payment_date: String(payment.payment_date || ''),
          brand: brand ? {
            full_name: String(brand.full_name || ''),
            company_name: String(brand.company_name || ''),
          } : undefined,
          campaign: campaign ? {
            title: String(campaign.title || ''),
          } : undefined,
          invoice_number: String(payment.invoice_number || ''),
          transaction_id: String(payment.transaction_id || ''),
        };
      });
    } catch (error) {
      console.error('Error getting payments:', error);
      return [];
    }
  },

  /**
   * Check if agency has completed onboarding
   */
  async checkOnboardingStatus(agencyUserId: string): Promise<{
    completed: boolean;
    completionPercentage: number;
    missingSteps: string[];
  }> {
    try {
      // Get profile data which contains metadata with services/about info
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('onboarding_completed, company_name, avatar_url, bio, metadata')
        .eq('id', agencyUserId)
        .single();

      // Also try to get agency settings
      const { data: settings } = await supabase
        .from('agency_settings')
        .select('company_name')
        .eq('agency_id', agencyUserId)
        .single();

      const metadata = (profile?.metadata as Record<string, unknown>) || {};

      // Check required steps
      const missingSteps: string[] = [];
      let completedSteps = 0;
      const totalSteps = 4;

      // Step 1: Agency name
      if (settings?.company_name || profile?.company_name) {
        completedSteps++;
      } else {
        missingSteps.push('agency_name');
      }

      // Step 2: Services offered (stored in metadata)
      const servicesOffered = metadata.services_offered as string[] | undefined;
      if (servicesOffered && Array.isArray(servicesOffered) && servicesOffered.length > 0) {
        completedSteps++;
      } else {
        missingSteps.push('services');
      }

      // Step 3: About agency (stored in bio or metadata)
      const aboutAgency = (metadata.about_agency as string) || profile?.bio;
      if (aboutAgency) {
        completedSteps++;
      } else {
        missingSteps.push('about');
      }

      // Step 4: Logo uploaded
      if (profile?.avatar_url) {
        completedSteps++;
      } else {
        missingSteps.push('logo');
      }

      const completionPercentage = Math.round((completedSteps / totalSteps) * 100);
      const completed = profile?.onboarding_completed === true || completionPercentage >= 75;

      return {
        completed,
        completionPercentage,
        missingSteps
      };
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      return {
        completed: false,
        completionPercentage: 0,
        missingSteps: ['agency_name', 'services', 'about', 'logo']
      };
    }
  },

  /**
   * Mark onboarding as completed
   */
  async markOnboardingCompleted(agencyUserId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ 
          onboarding_completed: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', agencyUserId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error marking onboarding completed:', error);
      return false;
    }
  },

  /**
   * Upload agency logo
   */
  async uploadAgencyLogo(
    agencyUserId: string,
    file: File
  ): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `agency-logos/${agencyUserId}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // Update user profile with new avatar URL
      await supabase
        .from('user_profiles')
        .update({ avatar_url: urlData.publicUrl })
        .eq('id', agencyUserId);

      // Also update agency settings
      await supabase
        .from('agency_settings')
        .upsert({
          agency_id: agencyUserId,
          agency_logo_url: urlData.publicUrl,
          updated_at: new Date().toISOString()
        });

      return { success: true, url: urlData.publicUrl };
    } catch (error) {
      console.error('Error uploading agency logo:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to upload logo' 
      };
    }
  },

  /**
   * Get agency settings
   */
  async getAgencySettings(agencyUserId: string): Promise<AgencySettings | null> {
    try {
      // Primary source: user_profiles table (always exists)
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('company_name, full_name, phone, bio, website, avatar_url, metadata')
        .eq('id', agencyUserId)
        .single();

      if (profileError) {
        console.warn('Error fetching user profile:', profileError.message);
      }

      // Try to get from agency_settings table (may not exist or have limited columns)
      let settingsData: Record<string, unknown> | null = null;
      try {
        const { data, error } = await supabase
          .from('agency_settings')
          .select('id, agency_id, company_name, company_logo')
          .eq('agency_id', agencyUserId)
          .maybeSingle();
        
        if (!error) {
          settingsData = data;
        }
      } catch {
        // Ignore errors from agency_settings table
      }

      // Merge data from both sources - prioritize user_profiles
      const metadata = (profileData?.metadata as Record<string, unknown>) || {};
      
      const mergedSettings: AgencySettings = {
        id: (settingsData?.id as string) || '',
        agency_id: agencyUserId,
        agency_name: (settingsData?.company_name as string) || profileData?.company_name || '',
        agency_logo_url: (settingsData?.company_logo as string) || profileData?.avatar_url,
        primary_contact_name: (metadata.primary_contact_name as string) || profileData?.full_name,
        primary_contact_email: (metadata.primary_contact_email as string),
        primary_email: (metadata.primary_email as string) || '',
        primary_contact_phone: (metadata.primary_contact_phone as string) || profileData?.phone,
        business_website: (metadata.business_website as string) || profileData?.website,
        business_phone: (metadata.business_phone as string),
        business_email: (metadata.business_email as string),
        business_address: (metadata.business_address as Record<string, unknown>),
        social_handles: (metadata.social_handles as Record<string, string>),
        about_agency: (metadata.about_agency as string) || profileData?.bio,
        services_offered: (metadata.services_offered as string[]) || [],
        industry_focus: (metadata.industry_focus as string[]) || [],
        specializations: (metadata.specializations as string[]),
        team_size: (metadata.team_size as string | number),
        years_in_business: (metadata.years_in_business as number),
        onboarding_completed: (metadata.onboarding_completed as boolean) || false,
        // GST & Taxation fields
        gstin: (metadata.gstin as string),
        legal_business_name: (metadata.legal_business_name as string),
        business_pan: (metadata.business_pan as string),
        gst_registration_date: (metadata.gst_registration_date as string),
        enable_gst_invoicing: (metadata.enable_gst_invoicing as boolean),
        auto_calculate_gst: (metadata.auto_calculate_gst as boolean),
        tds_applicable: (metadata.tds_applicable as boolean),
      };

      return mergedSettings;
    } catch (error) {
      console.error('Error getting agency settings:', error);
      return {
        id: '',
        agency_id: agencyUserId,
        agency_name: '',
      };
    }
  },

  /**
   * Update agency settings
   */
  async updateAgencySettings(agencyUserId: string, settings: Partial<AgencySettings>): Promise<boolean> {
    try {
      // Save all settings to user_profiles table which we know exists
      // This avoids issues with agency_settings table missing columns
      
      const profileUpdate: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      // Map directly to user_profiles columns
      if (settings.agency_name) profileUpdate.company_name = settings.agency_name;
      if (settings.about_agency) profileUpdate.bio = settings.about_agency;
      if (settings.primary_contact_name) profileUpdate.full_name = settings.primary_contact_name;
      if (settings.primary_contact_phone) profileUpdate.phone = settings.primary_contact_phone;
      if (settings.business_website) profileUpdate.website = settings.business_website;
      if (settings.agency_logo_url) profileUpdate.avatar_url = settings.agency_logo_url;
      // Save primary_email to user_profiles email field
      if (settings.primary_email) profileUpdate.email = settings.primary_email;

      // Store all additional data in metadata JSON field
      const metadata: Record<string, unknown> = {};
      if (settings.services_offered !== undefined) metadata.services_offered = settings.services_offered;
      if (settings.industry_focus !== undefined) metadata.industry_focus = settings.industry_focus;
      if (settings.team_size !== undefined) metadata.team_size = settings.team_size;
      if (settings.specializations !== undefined) metadata.specializations = settings.specializations;
      if (settings.years_in_business !== undefined) metadata.years_in_business = settings.years_in_business;
      if (settings.primary_contact_email !== undefined) metadata.primary_contact_email = settings.primary_contact_email;
      if (settings.primary_email !== undefined) metadata.primary_email = settings.primary_email;
      if (settings.business_phone !== undefined) metadata.business_phone = settings.business_phone;
      if (settings.business_email !== undefined) metadata.business_email = settings.business_email;
      if (settings.business_address !== undefined) metadata.business_address = settings.business_address;
      if (settings.social_handles !== undefined) metadata.social_handles = settings.social_handles;
      // GST & Taxation fields
      if (settings.gstin !== undefined) metadata.gstin = settings.gstin;
      if (settings.legal_business_name !== undefined) metadata.legal_business_name = settings.legal_business_name;
      if (settings.business_pan !== undefined) metadata.business_pan = settings.business_pan;
      if (settings.gst_registration_date !== undefined) metadata.gst_registration_date = settings.gst_registration_date;
      if (settings.enable_gst_invoicing !== undefined) metadata.enable_gst_invoicing = settings.enable_gst_invoicing;
      if (settings.auto_calculate_gst !== undefined) metadata.auto_calculate_gst = settings.auto_calculate_gst;
      if (settings.tds_applicable !== undefined) metadata.tds_applicable = settings.tds_applicable;
      
      // Get existing metadata and merge
      if (Object.keys(metadata).length > 0) {
        const { data: existingProfile } = await supabase
          .from('user_profiles')
          .select('metadata')
          .eq('id', agencyUserId)
          .single();
        
        const existingMetadata = (existingProfile?.metadata as Record<string, unknown>) || {};
        profileUpdate.metadata = { ...existingMetadata, ...metadata };
      }

      console.log('Saving agency settings:', profileUpdate);

      // Update user_profiles
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update(profileUpdate)
        .eq('id', agencyUserId);

      if (profileError) {
        console.error('Error updating user_profiles:', profileError.message);
        return false;
      }

      console.log('Agency settings saved successfully');

      // Try to update agency_settings table with minimal data (if it exists)
      try {
        const minimalSettings: Record<string, unknown> = {
          agency_id: agencyUserId,
          updated_at: new Date().toISOString(),
        };
        if (settings.agency_name) minimalSettings.agency_name = settings.agency_name;
        if (settings.agency_logo_url) minimalSettings.agency_logo_url = settings.agency_logo_url;
        
        await supabase
          .from('agency_settings')
          .upsert(minimalSettings);
      } catch {
        // Ignore errors from agency_settings - data is saved in user_profiles
      }

      return true;
    } catch (error) {
      console.error('Error updating agency settings:', error);
      return false;
    }
  },

  /**
   * Get recent activity for the agency
   */
  async getRecentActivity(agencyUserId: string, limit = 10) {
    try {
      const { data, error } = await supabase
        .from('agency_activity_log')
        .select('*')
        .eq('agency_id', agencyUserId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.warn('Agency activity log table may not exist:', error.message);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error getting recent activity:', error);
      return [];
    }
  },

  /**
   * Log an activity
   */
  async logActivity(
    agencyUserId: string,
    activityType: string,
    activityTitle: string,
    activityDescription?: string,
    relatedId?: string,
    relatedType?: string
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('agency_activity_log')
        .insert({
          agency_id: agencyUserId,
          activity_type: activityType,
          activity_title: activityTitle,
          activity_description: activityDescription,
          related_id: relatedId,
          metadata: {
            related_type: relatedType,
          },
        });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error logging activity:', error);
      return false;
    }
  },

  /**
   * Search for creators to add to network
   */
  async searchCreators(query: string, filters?: {
    category?: string;
    minFollowers?: number;
    maxFollowers?: number;
    minEngagement?: number;
  }) {
    try {
      let queryBuilder = supabase
        .from('user_profiles')
        .select('id, full_name, avatar_url, category, follower_count, engagement_rate, bio')
        .eq('role', 'creator')
        .ilike('full_name', `%${query}%`);

      if (filters?.category) {
        queryBuilder = queryBuilder.eq('category', filters.category);
      }
      if (filters?.minFollowers) {
        queryBuilder = queryBuilder.gte('follower_count', filters.minFollowers);
      }
      if (filters?.maxFollowers) {
        queryBuilder = queryBuilder.lte('follower_count', filters.maxFollowers);
      }
      if (filters?.minEngagement) {
        queryBuilder = queryBuilder.gte('engagement_rate', filters.minEngagement);
      }

      const { data, error } = await queryBuilder.limit(20);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching creators:', error);
      return [];
    }
  },

  /**
   * Search for influencers in the platform
   */
  async searchInfluencers(filters: {
    query?: string;
    category?: string;
    minFollowers?: number;
    maxFollowers?: number;
    minEngagement?: number;
    isVerified?: boolean;
  }): Promise<any[]> {
    try {
      let query = supabase
        .from('creator_profiles')
        .select(`
          *,
          user:user_profiles!inner(
            id,
            full_name,
            avatar_url,
            location
          )
        `);

      if (filters.query) {
        query = query.ilike('user.full_name', `%${filters.query}%`);
      }

      if (filters.category) {
        query = query.contains('categories', [filters.category]);
      }

      // Note: followers_count might be in different tables depending on platform (instagram_stats etc)
      // For now assuming a cached field in creator_profiles or strict join
      
      const { data, error } = await query;

      if (error) throw error;

      return data.map(creator => ({
        id: creator.id,
        name: creator.user.full_name,
        avatar: creator.user.avatar_url,
        username: creator.instagram_username || 'creator',
        followers: creator.total_followers || 0,
        engagement: creator.engagement_rate || 0,
        categories: creator.categories || [],
        isVerified: creator.is_verified || false,
        location: creator.user.location || 'Unknown',
        bio: creator.bio || '',
      }));
    } catch (error) {
      console.error('Error searching influencers:', error);
      return [];
    }
  },

  // ================================================================
  // NEW CAMPAIGN WORKFLOW - Fixed Payout Model (No CPV)
  // ================================================================

  /**
   * Get all clients managed by the agency
   */
  async getClients(agencyUserId: string): Promise<AgencyClient[]> {
    try {
      // Try with all columns first
      const { data, error } = await supabase
        .from('agency_clients')
        .select(`
          id,
          name,
          logo_url,
          industry,
          contact_email,
          contact_phone,
          created_at,
          total_brands,
          active_campaigns,
          total_spent
        `)
        .eq('agency_id', agencyUserId)
        .order('name', { ascending: true });

      if (error) {
        // If columns don't exist, try with basic columns
        if (error.message.includes('column') || error.code === '42703') {
          console.warn('Some columns missing in agency_clients, using basic query');
          const { data: basicData, error: basicError } = await supabase
            .from('agency_clients')
            .select('id, name, logo_url, industry, created_at')
            .eq('agency_id', agencyUserId)
            .order('name', { ascending: true });
          
          if (basicError) {
            console.warn('Agency clients table may not exist:', basicError.message);
            return [];
          }
          
          return (basicData || []).map((client: Record<string, unknown>) => ({
            id: String(client.id || ''),
            name: String(client.name || ''),
            logo: client.logo_url as string | undefined,
            industry: client.industry as string | undefined,
            contactEmail: undefined,
            contactPhone: undefined,
            createdAt: new Date(String(client.created_at || new Date().toISOString())),
            totalBrands: 0,
            activeCampaigns: 0,
            totalSpent: 0,
          }));
        }
        console.warn('Agency clients table may not exist:', error.message);
        return [];
      }

      return (data || []).map((client: Record<string, unknown>) => ({
        id: String(client.id || ''),
        name: String(client.name || ''),
        logo: client.logo_url as string | undefined,
        industry: client.industry as string | undefined,
        contactEmail: client.contact_email as string | undefined,
        contactPhone: client.contact_phone as string | undefined,
        createdAt: new Date(String(client.created_at || new Date().toISOString())),
        totalBrands: Number(client.total_brands || 0),
        activeCampaigns: Number(client.active_campaigns || 0),
        totalSpent: Number(client.total_spent || 0),
      }));
    } catch (error) {
      console.error('Error getting clients:', error);
      return [];
    }
  },

  /**
   * Create a new client
   */
  async createClient(agencyUserId: string, clientData: {
    name: string;
    industry?: string;
    contactEmail?: string;
    contactPhone?: string;
    logo?: string;
  }): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('agency_clients')
        .insert({
          agency_id: agencyUserId,
          name: clientData.name,
          industry: clientData.industry,
          contact_email: clientData.contactEmail,
          contact_phone: clientData.contactPhone,
          logo_url: clientData.logo,
        })
        .select('id')
        .single();

      if (error) throw error;
      return data?.id || null;
    } catch (error) {
      console.error('Error creating client:', error);
      return null;
    }
  },

  /**
   * Get brands for a specific client
   */
  async getClientBrands(clientId: string): Promise<AgencyBrandItem[]> {
    try {
      const { data, error } = await supabase
        .from('agency_client_brands')
        .select(`
          id,
          client_id,
          name,
          logo_url,
          category,
          website,
          created_at,
          active_campaigns
        `)
        .eq('client_id', clientId)
        .order('name', { ascending: true });

      if (error) {
        console.warn('Agency client brands table may not exist:', error.message);
        return [];
      }

      return (data || []).map((brand: Record<string, unknown>) => ({
        id: String(brand.id || ''),
        clientId: String(brand.client_id || ''),
        name: String(brand.name || ''),
        logo: brand.logo_url as string | undefined,
        category: brand.category as string | undefined,
        website: brand.website as string | undefined,
        createdAt: new Date(String(brand.created_at || new Date().toISOString())),
        activeCampaigns: Number(brand.active_campaigns || 0),
      }));
    } catch (error) {
      console.error('Error getting client brands:', error);
      return [];
    }
  },

  /**
   * Create a new brand for a client
   */
  async createClientBrand(clientId: string, brandData: {
    name: string;
    category?: string;
    website?: string;
    logo?: string;
  }): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('agency_client_brands')
        .insert({
          client_id: clientId,
          name: brandData.name,
          category: brandData.category,
          website: brandData.website,
          logo_url: brandData.logo,
        })
        .select('id')
        .single();

      if (error) throw error;
      return data?.id || null;
    } catch (error) {
      console.error('Error creating client brand:', error);
      return null;
    }
  },

  /**
   * Create a new campaign with fixed payout model or barter
   */
  async createCampaign(agencyUserId: string, campaignData: CampaignCreateData): Promise<string | null> {
    try {
      const isBarter = campaignData.campaignMode === 'barter';
      
      const { data, error } = await supabase
        .from('agency_campaigns')
        .insert({
          agency_id: agencyUserId,
          client_id: campaignData.clientId,
          brand_id: campaignData.brandId,
          title: campaignData.title,
          description: campaignData.description,
          category: campaignData.category,
          status: 'draft',
          total_budget: isBarter ? 0 : campaignData.totalBudget,
          max_payout_per_influencer: isBarter ? 0 : campaignData.maxPayoutPerInfluencer,
          estimated_influencers: campaignData.estimatedInfluencers,
          start_date: campaignData.startDate,
          end_date: campaignData.endDate,
          deliverables: campaignData.deliverables,
          content_guidelines: campaignData.contentGuidelines,
          payment_type: isBarter ? 'barter' : campaignData.paymentType,
          image_url: campaignData.image,
          // Barter fields
          campaign_mode: campaignData.campaignMode || 'paid',
          product_name: campaignData.productName,
          product_value: campaignData.productValue ? Number(campaignData.productValue) : null,
          product_units: campaignData.productUnits ? Number(campaignData.productUnits) : null,
          delivery_instructions: campaignData.deliveryInstructions,
          require_shipping_address: campaignData.requireShippingAddress || false,
          estimated_delivery_days: campaignData.estimatedDeliveryDays,
          shipping_provider: campaignData.shippingProvider,
        })
        .select('id')
        .single();

      if (error) throw error;
      
      // Log activity
      await this.logActivity(
        agencyUserId,
        'campaign_created',
        'New campaign created',
        `Campaign "${campaignData.title}" has been created as draft`,
        data?.id,
        'campaign'
      );

      return data?.id || null;
    } catch (error) {
      console.error('Error creating campaign:', error);
      return null;
    }
  },

  /**
   * Update campaign status
   */
  async updateCampaignStatus(campaignId: string, status: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('agency_campaigns')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', campaignId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating campaign status:', error);
      return false;
    }
  },

  /**
   * Get campaigns with new fixed payout model
   */
  async getCampaignsWithPayout(agencyUserId: string, filters?: {
    status?: string;
    clientId?: string;
    brandId?: string;
  }): Promise<CampaignWithPayout[]> {
    try {
      // First try with joins - if foreign keys exist
      let query = supabase
        .from('agency_campaigns')
        .select(`
          id,
          title,
          description,
          client_id,
          brand_id,
          status,
          total_budget,
          max_payout_per_influencer,
          estimated_influencers,
          budget_spent,
          start_date,
          end_date,
          category,
          deliverables,
          content_guidelines,
          payment_type,
          image_url,
          created_at,
          updated_at,
          invited_influencers,
          accepted_influencers,
          pending_deliverables,
          completed_deliverables,
          campaign_mode,
          product_name,
          product_value,
          product_units,
          delivery_instructions,
          require_shipping_address,
          estimated_delivery_days,
          shipping_provider
        `)
        .eq('agency_id', agencyUserId)
        .order('created_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.clientId) {
        query = query.eq('client_id', filters.clientId);
      }
      if (filters?.brandId) {
        query = query.eq('brand_id', filters.brandId);
      }

      const { data, error } = await query;

      if (error) {
        console.warn('Agency campaigns query error:', error.message);
        return [];
      }

      // Fetch client and brand names separately if we have IDs
      const clientIds = [...new Set((data || []).map(c => c.client_id).filter(Boolean))];
      const brandIds = [...new Set((data || []).map(c => c.brand_id).filter(Boolean))];
      
      let clientMap = new Map<string, string>();
      let brandMap = new Map<string, string>();
      
      if (clientIds.length > 0) {
        const { data: clients } = await supabase
          .from('agency_clients')
          .select('id, name')
          .in('id', clientIds);
        (clients || []).forEach((c: { id: string; name: string }) => clientMap.set(c.id, c.name));
      }
      
      if (brandIds.length > 0) {
        const { data: brands } = await supabase
          .from('agency_client_brands')
          .select('id, name')
          .in('id', brandIds);
        (brands || []).forEach((b: { id: string; name: string }) => brandMap.set(b.id, b.name));
      }

      return (data || []).map((campaign: Record<string, unknown>) => {
        const clientId = String(campaign.client_id || '');
        const brandId = String(campaign.brand_id || '');
        return {
          id: String(campaign.id || ''),
          title: String(campaign.title || ''),
          description: String(campaign.description || ''),
          clientId: clientId,
          brandId: brandId,
          clientName: clientMap.get(clientId) || 'Unknown Client',
          brandName: brandMap.get(brandId) || 'Unknown Brand',
          status: String(campaign.status || 'draft') as 'draft' | 'active' | 'completed' | 'rejected',
          totalBudget: Number(campaign.total_budget || 0),
          maxPayoutPerInfluencer: Number(campaign.max_payout_per_influencer || 0),
          estimatedInfluencers: Number(campaign.estimated_influencers || 0),
          budgetSpent: Number(campaign.budget_spent || 0),
          startDate: new Date(String(campaign.start_date || new Date().toISOString())),
          endDate: new Date(String(campaign.end_date || new Date().toISOString())),
          category: String(campaign.category || ''),
          deliverables: campaign.deliverables as DeliverableItem[] || [],
          contentGuidelines: String(campaign.content_guidelines || ''),
          paymentType: String(campaign.payment_type || 'agency_wallet') as 'agency_wallet' | 'pay_per_influencer',
          image: campaign.image_url as string | undefined,
          createdAt: new Date(String(campaign.created_at || new Date().toISOString())),
          updatedAt: new Date(String(campaign.updated_at || new Date().toISOString())),
          invitedInfluencers: Number(campaign.invited_influencers || 0),
          acceptedInfluencers: Number(campaign.accepted_influencers || 0),
          pendingDeliverables: Number(campaign.pending_deliverables || 0),
          completedDeliverables: Number(campaign.completed_deliverables || 0),
          // Barter fields
          campaignMode: (campaign.campaign_mode as 'paid' | 'barter') || 'paid',
          productName: campaign.product_name as string | undefined,
          productValue: campaign.product_value ? Number(campaign.product_value) : undefined,
          productUnits: campaign.product_units ? Number(campaign.product_units) : undefined,
          deliveryInstructions: campaign.delivery_instructions as string | undefined,
          requireShippingAddress: Boolean(campaign.require_shipping_address),
          estimatedDeliveryDays: campaign.estimated_delivery_days as string | undefined,
          shippingProvider: campaign.shipping_provider as string | undefined,
        };
      });
    } catch (error) {
      console.error('Error getting campaigns with payout:', error);
      return [];
    }
  },

  // ================================================================
  // INFLUENCER INVITATIONS
  // ================================================================

  /**
   * Send invitation to influencer for a campaign
   */
  async sendInfluencerInvitation(campaignId: string, influencerId: string, payout: number, message?: string): Promise<string | null> {
    try {
      // First check if invitation already exists for this campaign + creator
      const { data: existingInvitation } = await supabase
        .from('campaign_invitations')
        .select('id, status')
        .eq('campaign_id', campaignId)
        .eq('creator_id', influencerId)
        .maybeSingle();
      
      if (existingInvitation) {
        // Get campaign details to update invitation with correct IDs
        let updateData: Record<string, any> = { 
          status: 'pending', 
          payout: payout > 0 ? payout : null,
          created_at: new Date().toISOString()
        };
        
        // Try to find the campaign and get associated IDs
        const { data: regularCampaign } = await supabase
          .from('campaigns')
          .select('brand_id')
          .eq('id', campaignId)
          .maybeSingle();
        
        if (regularCampaign?.brand_id) {
          updateData.brand_id = regularCampaign.brand_id;
        } else {
          // Try agency_campaigns
          const { data: agencyCampaign } = await supabase
            .from('agency_campaigns')
            .select('agency_id, brand_id')
            .eq('id', campaignId)
            .maybeSingle();
          
          if (agencyCampaign) {
            updateData.agency_id = agencyCampaign.agency_id;
            if (agencyCampaign.brand_id) {
              updateData.brand_id = agencyCampaign.brand_id;
            }
          }
        }
        
        const { error: updateError } = await supabase
          .from('campaign_invitations')
          .update(updateData)
          .eq('id', existingInvitation.id);
        
        if (updateError) throw updateError;
        return existingInvitation.id;
      }
      
      // First try to find in regular campaigns table
      const { data: campaign, error: campaignError } = await supabase
        .from('campaigns')
        .select('brand_id')
        .eq('id', campaignId)
        .maybeSingle();
      
      if (campaign?.brand_id) {
        // Regular brand campaign - use brand_id
        const insertData: Record<string, any> = {
          campaign_id: campaignId,
          creator_id: influencerId,
          brand_id: campaign.brand_id,
          status: 'pending',
        };
        
        if (payout > 0) {
          insertData.payout = payout;
        }
        
        const { data, error } = await supabase
          .from('campaign_invitations')
          .insert(insertData)
          .select('id')
          .single();

        if (error) throw error;
        return data?.id || null;
      }
      
      // If not found in campaigns, try agency_campaigns
      const { data: agencyCampaign, error: agencyError } = await supabase
        .from('agency_campaigns')
        .select('agency_id, brand_id')
        .eq('id', campaignId)
        .maybeSingle();
      
      if (agencyError) {
        console.error('Error fetching agency campaign:', agencyError);
        throw new Error('Could not find campaign');
      }
      
      if (!agencyCampaign) {
        throw new Error('Campaign not found in either campaigns or agency_campaigns table');
      }
      
      // Agency campaign - use agency_id (brand_id is optional for agencies)
      const insertData: Record<string, any> = {
        campaign_id: campaignId,
        creator_id: influencerId,
        agency_id: agencyCampaign.agency_id,
        status: 'pending',
      };
      
      // Also include brand_id if the agency campaign has one
      if (agencyCampaign.brand_id) {
        insertData.brand_id = agencyCampaign.brand_id;
      }
      
      if (payout > 0) {
        insertData.payout = payout;
      }
      
      const { data, error } = await supabase
        .from('campaign_invitations')
        .insert(insertData)
        .select('id')
        .single();
      
      if (error) throw error;
      return data?.id || null;
    } catch (error) {
      console.error('Error sending invitation:', error);
      return null;
    }
  },

  /**
   * Get invitations for a campaign
   */
  async getCampaignInvitations(campaignId: string): Promise<CampaignInvitationItem[]> {
    try {
      const { data, error } = await supabase
        .from('campaign_invitations')
        .select(`
          id,
          campaign_id,
          creator_id,
          status,
          offered_payout,
          created_at
        `)
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get creator details separately
      const creatorIds = (data || []).map((inv: any) => inv.creator_id).filter(Boolean);
      let creatorsMap = new Map<string, any>();
      
      if (creatorIds.length > 0) {
        const { data: creators } = await supabase
          .from('creator_profiles')
          .select('user_id, username, followers, user_profiles:user_id(full_name, avatar_url)')
          .in('user_id', creatorIds);
        
        (creators || []).forEach((c: any) => {
          creatorsMap.set(c.user_id, c);
        });
      }

      return (data || []).map((inv: Record<string, unknown>) => {
        const creator = creatorsMap.get(String(inv.creator_id));
        return {
          id: String(inv.id || ''),
          campaignId: String(inv.campaign_id || ''),
          influencerId: String(inv.creator_id || ''),
          influencerName: String(creator?.user_profiles?.full_name || creator?.username || 'Unknown'),
          influencerAvatar: creator?.user_profiles?.avatar_url as string | undefined,
          influencerFollowers: Number(creator?.followers || 0),
          status: String(inv.status || 'pending') as 'pending' | 'accepted' | 'declined' | 'expired' | 'cancelled',
          offeredPayout: Number(inv.offered_payout || 0),
          message: undefined,
          sentAt: new Date(String(inv.created_at || new Date().toISOString())),
          respondedAt: undefined,
          expiresAt: undefined,
        };
      });
    } catch (error) {
      console.error('Error getting campaign invitations:', error);
      return [];
    }
  },

  /**
   * Cancel an invitation
   */
  async cancelInvitation(invitationId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('campaign_invitations')
        .update({ status: 'cancelled' })
        .eq('id', invitationId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      return false;
    }
  },

  // ================================================================
  // DELIVERABLES MANAGEMENT
  // ================================================================

  /**
   * Get deliverables for a campaign
   */
  async getCampaignDeliverables(campaignId: string): Promise<DeliverableData[]> {
    try {
      const { data, error } = await supabase
        .from('agency_deliverables')
        .select(`

          id,
          campaign_id,
          influencer_id,
          type,
          status,
          draft_url,
          final_url,
          screenshot_url,
          submitted_at,
          approved_at,
          rejected_at,
          rejection_reason,
          payout,
          views,
          likes,
          comments,
          shares,
          engagement_rate,
          influencer:user_profiles!agency_deliverables_influencer_id_fkey(
            full_name,
            avatar_url
          )
        `)
        .eq('campaign_id', campaignId)
        .order('submitted_at', { ascending: false });

      if (error) throw error;

      return (data || []).map((del: Record<string, unknown>) => {
        const influencer = del.influencer as Record<string, unknown> | null;
        return {
          id: String(del.id || ''),
          campaignId: String(del.campaign_id || ''),
          influencerId: String(del.influencer_id || ''),
          influencerName: String(influencer?.full_name || 'Unknown'),
          influencerAvatar: influencer?.avatar_url as string | undefined,
          type: String(del.type || 'post') as 'reel' | 'post' | 'story' | 'video' | 'blog',
          status: String(del.status || 'pending') as 'pending' | 'draft_submitted' | 'draft_approved' | 'draft_rejected' | 'submitted' | 'approved' | 'rejected',
          draftUrl: del.draft_url as string | undefined,
          finalUrl: del.final_url as string | undefined,
          screenshotUrl: del.screenshot_url as string | undefined,
          submittedAt: del.submitted_at ? new Date(String(del.submitted_at)) : undefined,
          approvedAt: del.approved_at ? new Date(String(del.approved_at)) : undefined,
          rejectedAt: del.rejected_at ? new Date(String(del.rejected_at)) : undefined,
          rejectionReason: del.rejection_reason as string | undefined,
          payout: Number(del.payout || 0),
          views: Number(del.views || 0),
          likes: Number(del.likes || 0),
          comments: Number(del.comments || 0),
          shares: Number(del.shares || 0),
          engagementRate: Number(del.engagement_rate || 0),
        };
      });
    } catch (error) {
      console.error('Error getting campaign deliverables:', error);
      return [];
    }
  },

  /**
   * Approve a deliverable draft
   */
  async approveDraft(deliverableId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('agency_deliverables')
        .update({ 
          status: 'draft_approved',
          approved_at: new Date().toISOString()
        })
        .eq('id', deliverableId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error approving draft:', error);
      return false;
    }
  },

  /**
   * Reject a deliverable draft
   */
  async rejectDraft(deliverableId: string, reason: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('agency_deliverables')
        .update({ 
          status: 'draft_rejected',
          rejection_reason: reason,
          rejected_at: new Date().toISOString()
        })
        .eq('id', deliverableId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error rejecting draft:', error);
      return false;
    }
  },

  /**
   * Approve final deliverable and process payment
   */
  async approveDeliverable(deliverableId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('agency_deliverables')
        .update({ 
          status: 'approved',
          approved_at: new Date().toISOString()
        })
        .eq('id', deliverableId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error approving deliverable:', error);
      return false;
    }
  },

  /**
   * Reject final deliverable
   */
  async rejectDeliverable(deliverableId: string, reason: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('agency_deliverables')
        .update({ 
          status: 'rejected',
          rejection_reason: reason,
          rejected_at: new Date().toISOString()
        })
        .eq('id', deliverableId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error rejecting deliverable:', error);
      return false;
    }
  },

  // ================================================================
  // SHORTLIST MANAGEMENT
  // ================================================================

  /**
   * Add influencer to campaign shortlist
   */
  async addToShortlist(campaignId: string, influencerId: string, proposedPayout: number, notes?: string): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('agency_campaign_shortlist')
        .insert({
          campaign_id: campaignId,
          influencer_id: influencerId,
          proposed_payout: proposedPayout,
          notes: notes,
          status: 'shortlisted',
        })
        .select('id')
        .single();

      if (error) throw error;
      return data?.id || null;
    } catch (error) {
      console.error('Error adding to shortlist:', error);
      return null;
    }
  },

  /**
   * Get campaign shortlist
   */
  async getCampaignShortlist(campaignId: string): Promise<ShortlistItem[]> {
    try {
      const { data, error } = await supabase
        .from('agency_campaign_shortlist')
        .select(`
          id,
          campaign_id,
          influencer_id,
          proposed_payout,
          notes,
          status,
          created_at,
          influencer:user_profiles!agency_campaign_shortlist_influencer_id_fkey(
            id,
            full_name,
            avatar_url,
            category,
            follower_count,
            engagement_rate
          )
        `)
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return (data || []).map((item: Record<string, unknown>) => {
        const influencer = item.influencer as Record<string, unknown> | null;
        return {
          id: String(item.id || ''),
          campaignId: String(item.campaign_id || ''),
          influencerId: String(item.influencer_id || ''),
          proposedPayout: Number(item.proposed_payout || 0),
          notes: item.notes as string | undefined,
          status: String(item.status || 'shortlisted') as 'shortlisted' | 'invited' | 'removed',
          addedAt: new Date(String(item.created_at || new Date().toISOString())),
          influencer: influencer ? {
            id: String(influencer.id || ''),
            name: String(influencer.full_name || ''),
            avatar: influencer.avatar_url as string | undefined,
            category: (influencer.category as string[]) || [],
            followers: Number(influencer.follower_count || 0),
            engagementRate: Number(influencer.engagement_rate || 0),
          } : null,
        };
      });
    } catch (error) {
      console.error('Error getting campaign shortlist:', error);
      return [];
    }
  },

  /**
   * Remove from shortlist
   */
  async removeFromShortlist(shortlistId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('agency_campaign_shortlist')
        .update({ status: 'removed' })
        .eq('id', shortlistId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error removing from shortlist:', error);
      return false;
    }
  },

  // ================================================================
  // REPORTING (Performance only, not tied to payments)
  // ================================================================

  /**
   * Get campaign performance report
   */
  async getCampaignReport(campaignId: string): Promise<CampaignReportData | null> {
    try {
      // Get campaign details
      const { data: campaign, error: campaignError } = await supabase
        .from('agency_campaigns')
        .select(`
          id,
          title,
          status,
          total_budget,
          budget_spent,
          start_date,
          end_date,
          client:agency_clients!agency_campaigns_client_id_fkey(name),
          brand:agency_client_brands!agency_campaigns_brand_id_fkey(name)
        `)
        .eq('id', campaignId)
        .single();

      if (campaignError) throw campaignError;

      // Get deliverables performance
      const { data: deliverables } = await supabase
        .from('agency_deliverables')
        .select('views, likes, comments, shares, engagement_rate, status')
        .eq('campaign_id', campaignId);

      const completedDeliverables = (deliverables || []).filter(d => d.status === 'approved');
      const totalViews = completedDeliverables.reduce((sum, d) => sum + (d.views || 0), 0);
      const totalLikes = completedDeliverables.reduce((sum, d) => sum + (d.likes || 0), 0);
      const totalComments = completedDeliverables.reduce((sum, d) => sum + (d.comments || 0), 0);
      const totalShares = completedDeliverables.reduce((sum, d) => sum + (d.shares || 0), 0);
      const avgEngagement = completedDeliverables.length > 0
        ? completedDeliverables.reduce((sum, d) => sum + (d.engagement_rate || 0), 0) / completedDeliverables.length
        : 0;

      // Get influencer count
      const { count: influencerCount } = await supabase
        .from('campaign_invitations')
        .select('id', { count: 'exact', head: true })
        .eq('campaign_id', campaignId)
        .eq('status', 'accepted');

      const client = campaign.client as Record<string, unknown> | null;
      const brand = campaign.brand as Record<string, unknown> | null;

      return {
        campaignId: String(campaign.id),
        campaignTitle: String(campaign.title || ''),
        clientName: String(client?.name || 'Unknown'),
        brandName: String(brand?.name || 'Unknown'),
        status: String(campaign.status) as 'draft' | 'active' | 'completed' | 'rejected',
        startDate: new Date(String(campaign.start_date)),
        endDate: new Date(String(campaign.end_date)),
        budget: Number(campaign.total_budget || 0),
        spent: Number(campaign.budget_spent || 0),
        influencers: influencerCount || 0,
        deliverables: deliverables?.length || 0,
        completedDeliverables: completedDeliverables.length,
        performance: {
          totalViews,
          totalLikes,
          totalComments,
          totalShares,
          averageEngagementRate: avgEngagement,
          reachEstimate: totalViews * 1.2, // Estimated reach
        },
      };
    } catch (error) {
      console.error('Error getting campaign report:', error);
      return null;
    }
  },

  /**
   * Get influencer performance report
   */
  async getInfluencerReports(agencyUserId: string): Promise<InfluencerReportData[]> {
    try {
      // First try to get data from agency_deliverables (preferred)
      const { data: deliverables, error: delError } = await supabase
        .from('agency_deliverables')
        .select(`
          influencer_id,
          payout_amount,
          status,
          engagement_rate,
          influencer:user_profiles!agency_deliverables_influencer_id_fkey(
            full_name,
            avatar_url,
            follower_count
          )
        `)
        .eq('agency_id', agencyUserId);

      if (!delError && deliverables && deliverables.length > 0) {
        // Use agency_deliverables data
        const influencerMap = new Map<string, InfluencerReportData>();
        
        deliverables.forEach((del: Record<string, unknown>) => {
          const influencer = del.influencer as Record<string, unknown> | null;
          const influencerId = String(del.influencer_id);

          if (!influencerMap.has(influencerId)) {
            influencerMap.set(influencerId, {
              influencerId,
              influencerName: String(influencer?.full_name || 'Unknown'),
              influencerAvatar: influencer?.avatar_url as string | undefined,
              platform: 'instagram',
              followers: Number(influencer?.follower_count || 0),
              campaignsCompleted: 0,
              totalEarnings: 0,
              deliverablesCompleted: 0,
              averageEngagement: 0,
              performanceScore: 0,
            });
          }

          const report = influencerMap.get(influencerId)!;
          if (del.status === 'approved') {
            report.deliverablesCompleted++;
            report.campaignsCompleted++;
          }
          report.totalEarnings += Number(del.payout_amount || 0);
          report.averageEngagement = Number(del.engagement_rate || 0);
        });

        return Array.from(influencerMap.values());
      }

      // Fallback: Get from campaign_invitations with simpler query
      const { data, error } = await supabase
        .from('campaign_invitations')
        .select(`
          creator_id,
          offered_payout,
          status,
          creator:user_profiles!campaign_invitations_creator_id_fkey(
            full_name,
            avatar_url,
            follower_count
          )
        `)
        .eq('status', 'accepted');

      if (error) {
        console.warn('Error fetching invitations, returning empty:', error.message);
        return [];
      }

      // Aggregate by creator
      const influencerMap = new Map<string, InfluencerReportData>();
      
      (data || []).forEach((inv: Record<string, unknown>) => {
        const influencer = inv.creator as Record<string, unknown> | null;
        const influencerId = String(inv.creator_id);

        if (!influencerMap.has(influencerId)) {
          influencerMap.set(influencerId, {
            influencerId,
            influencerName: String(influencer?.full_name || 'Unknown'),
            influencerAvatar: influencer?.avatar_url as string | undefined,
            platform: 'instagram',
            followers: Number(influencer?.follower_count || 0),
            campaignsCompleted: 0,
            totalEarnings: 0,
            deliverablesCompleted: 0,
            averageEngagement: 0,
            performanceScore: 0,
          });
        }

        const report = influencerMap.get(influencerId)!;
        report.campaignsCompleted++;
        report.totalEarnings += Number(inv.offered_payout || 0);
      });

      return Array.from(influencerMap.values());
    } catch (error) {
      console.error('Error getting influencer reports:', error);
      return [];
    }
  },

  /**
   * Discover influencers for campaigns
   */
  async discoverInfluencers(filters?: {
    category?: string;
    platform?: string;
    minFollowers?: number;
    maxFollowers?: number;
    minEngagement?: number;
    isVerified?: boolean;
    search?: string;
  }): Promise<DiscoveredInfluencer[]> {
    try {
      // Query creator_profiles table with user_profiles join for full name and avatar
      let query = supabase
        .from('creator_profiles')
        .select(`
          id,
          user_id,
          username,
          category,
          followers,
          engagement_rate,
          bio,
          rating,
          user_profiles:user_id(full_name, avatar_url, metadata)
        `);

      if (filters?.search) {
        query = query.ilike('username', `%${filters.search}%`);
      }
      if (filters?.category) {
        // Use ilike for partial matching if category is text, or contains if it's an array
        // Assuming text based on schema
        query = query.ilike('category', `%${filters.category}%`);
      }
      if (filters?.minFollowers) {
        query = query.gte('followers', filters.minFollowers);
      }
      if (filters?.maxFollowers) {
        query = query.lte('followers', filters.maxFollowers);
      }
      if (filters?.minEngagement) {
        query = query.gte('engagement_rate', filters.minEngagement);
      }

      const { data, error } = await query.limit(50);

      if (error) {
        console.error('Error discovering influencers:', error);
        return [];
      }

      console.log('Discovered influencers:', data?.length || 0);

      return (data || []).map((inf: any) => ({
        id: String(inf.user_id || inf.id || ''),
        name: String(inf.user_profiles?.full_name || inf.username || 'Creator'),
        username: String(inf.username || ''),
        avatar: inf.user_profiles?.avatar_url as string | undefined,
        platform: 'instagram' as const,
        followers: Number(inf.followers || 0),
        engagementRate: Number(inf.engagement_rate || 0),
        averageViews: Math.round(Number(inf.followers || 0) * (Number(inf.engagement_rate || 0) / 100)),
        category: Array.isArray(inf.category) ? inf.category : (inf.category ? [inf.category] : []),
        rating: Number(inf.rating || 0),
        completedCampaigns: 0,
        isVerified: true,
        location: (inf.user_profiles?.metadata?.location || inf.location) as string | undefined,
        bio: inf.bio as string | undefined,
      }));
    } catch (error) {
      console.error('Error discovering influencers:', error);
      return [];
    }
  },
};

// Additional type definitions for new functions
export interface AgencyClient {
  id: string;
  name: string;
  logo?: string;
  industry?: string;
  contactEmail?: string;
  contactPhone?: string;
  createdAt: Date;
  totalBrands: number;
  activeCampaigns: number;
  totalSpent: number;
}

export interface AgencyBrandItem {
  id: string;
  clientId: string;
  name: string;
  logo?: string;
  category?: string;
  website?: string;
  createdAt: Date;
  activeCampaigns: number;
}

export interface CampaignCreateData {
  clientId: string;
  brandId: string;
  title: string;
  description: string;
  category: string;
  totalBudget: number;
  maxPayoutPerInfluencer: number;
  estimatedInfluencers: number;
  startDate: string;
  endDate: string;
  deliverables: DeliverableItem[];
  contentGuidelines: string;
  paymentType: 'agency_wallet' | 'per_view' | 'fixed_amount' | 'per_influencer' | 'pay_per_influencer';
  image?: string;
  // Barter campaign fields
  campaignMode?: 'paid' | 'barter';
  productName?: string;
  productValue?: string;
  productUnits?: string;
  deliveryInstructions?: string;
  requireShippingAddress?: boolean;
  estimatedDeliveryDays?: string;
  shippingProvider?: string;
}

export interface DeliverableItem {
  id: string;
  type: string;
  quantity: number;
  description?: string;
}

export interface CampaignWithPayout {
  id: string;
  title: string;
  description: string;
  clientId: string;
  brandId: string;
  clientName: string;
  brandName: string;
  status: 'draft' | 'active' | 'completed' | 'rejected';
  totalBudget: number;
  maxPayoutPerInfluencer: number;
  estimatedInfluencers: number;
  budgetSpent: number;
  startDate: Date;
  endDate: Date;
  category: string;
  deliverables: DeliverableItem[];
  contentGuidelines: string;
  paymentType: 'agency_wallet' | 'pay_per_influencer';
  image?: string;
  createdAt: Date;
  updatedAt: Date;
  invitedInfluencers: number;
  acceptedInfluencers: number;
  pendingDeliverables: number;
  completedDeliverables: number;
  // Barter campaign fields
  campaignMode?: 'paid' | 'barter';
  productName?: string;
  productValue?: number;
  productUnits?: number;
  deliveryInstructions?: string;
  requireShippingAddress?: boolean;
  estimatedDeliveryDays?: string;
  shippingProvider?: string;
}

export interface CampaignInvitationItem {
  id: string;
  campaignId: string;
  influencerId: string;
  influencerName: string;
  influencerAvatar?: string;
  influencerFollowers: number;
  status: 'pending' | 'accepted' | 'declined' | 'expired' | 'cancelled';
  offeredPayout: number;
  message?: string;
  sentAt: Date;
  respondedAt?: Date;
  expiresAt?: Date;
}

export interface DeliverableData {
  id: string;
  campaignId: string;
  influencerId: string;
  influencerName: string;
  influencerAvatar?: string;
  type: 'reel' | 'post' | 'story' | 'video' | 'blog';
  status: 'pending' | 'draft_submitted' | 'draft_approved' | 'draft_rejected' | 'submitted' | 'approved' | 'rejected';
  draftUrl?: string;
  finalUrl?: string;
  screenshotUrl?: string;
  submittedAt?: Date;
  approvedAt?: Date;
  rejectedAt?: Date;
  rejectionReason?: string;
  payout: number;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  engagementRate: number;
}

export interface ShortlistItem {
  id: string;
  campaignId: string;
  influencerId: string;
  proposedPayout: number;
  notes?: string;
  status: 'shortlisted' | 'invited' | 'removed';
  addedAt: Date;
  influencer: {
    id: string;
    name: string;
    avatar?: string;
    category: string[];
    followers: number;
    engagementRate: number;
  } | null;
}

export interface CampaignReportData {
  campaignId: string;
  campaignTitle: string;
  clientName: string;
  brandName: string;
  status: 'draft' | 'active' | 'completed' | 'rejected';
  startDate: Date;
  endDate: Date;
  budget: number;
  spent: number;
  influencers: number;
  deliverables: number;
  completedDeliverables: number;
  performance: {
    totalViews: number;
    totalLikes: number;
    totalComments: number;
    totalShares: number;
    averageEngagementRate: number;
    reachEstimate: number;
  };
}

export interface InfluencerReportData {
  influencerId: string;
  influencerName: string;
  influencerAvatar?: string;
  platform: string;
  followers: number;
  campaignsCompleted: number;
  totalEarnings: number;
  deliverablesCompleted: number;
  averageEngagement: number;
  performanceScore: number;
}

export interface DiscoveredInfluencer {
  id: string;
  name: string;
  username: string;
  avatar?: string;
  platform: 'instagram' | 'youtube' | 'tiktok' | 'twitter';
  followers: number;
  engagementRate: number;
  averageViews: number;
  category: string[];
  rating: number;
  completedCampaigns: number;
  isVerified: boolean;
  location?: string;
  bio?: string;
}

export interface TrendDataPoint {
  label: string;
  value: number;
  previousValue?: number;
}

export interface PerformanceTrendsData {
  revenueData: TrendDataPoint[];
  deliverableData: TrendDataPoint[];
  creatorData: TrendDataPoint[];
}

// Add getPerformanceTrends to agencyService
Object.assign(agencyService, {
  /**
   * Get performance trends data for the agency dashboard
   * Returns monthly revenue, deliverable, and creator trend data
   */
  async getPerformanceTrends(agencyUserId: string, months: number = 6): Promise<PerformanceTrendsData> {
    try {
      const now = new Date();
      const monthLabels = [];
      const startDates = [];
      
      // Generate month labels and date ranges
      for (let i = months - 1; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        monthLabels.push(date.toLocaleString('default', { month: 'short' }));
        startDates.push({
          start: new Date(date.getFullYear(), date.getMonth(), 1),
          end: new Date(date.getFullYear(), date.getMonth() + 1, 0),
        });
      }

      // Fetch campaigns created by this agency
      const { data: campaigns } = await supabase
        .from('campaigns')
        .select('id, created_at, budget')
        .eq('brand_id', agencyUserId);

      // Define types for the fetched data
      type CampaignRow = { id: string; created_at: string; budget: number };
      type DeliverableRow = { id: string; created_at: string; status: string; campaign_id: string };
      type ApplicationRow = { id: string; created_at: string; status: string; campaign_id: string };

      const campaignsData = (campaigns || []) as CampaignRow[];

      // Fetch deliverables data
      const { data: deliverables } = await supabase
        .from('deliverables')
        .select('id, created_at, status, campaign_id')
        .in('campaign_id', campaignsData.map((c: CampaignRow) => c.id));

      // Fetch creator applications/invitations
      const { data: applications } = await supabase
        .from('campaign_applications')
        .select('id, created_at, status, campaign_id')
        .in('campaign_id', campaignsData.map((c: CampaignRow) => c.id));

      const deliverablesData = (deliverables || []) as DeliverableRow[];
      const applicationsData = (applications || []) as ApplicationRow[];

      // Process data into monthly trends
      const revenueData: TrendDataPoint[] = [];
      const deliverableData: TrendDataPoint[] = [];
      const creatorData: TrendDataPoint[] = [];

      for (let i = 0; i < months; i++) {
        const { start, end } = startDates[i];
        
        // Revenue (campaigns budget for that month)
        const monthCampaigns = campaignsData.filter((c: CampaignRow) => {
          const createdAt = new Date(c.created_at);
          return createdAt >= start && createdAt <= end;
        });
        const monthRevenue = monthCampaigns.reduce((sum: number, c: CampaignRow) => sum + (c.budget || 0), 0);
        
        // Deliverables completed that month
        const monthDeliverables = deliverablesData.filter((d: DeliverableRow) => {
          const createdAt = new Date(d.created_at);
          return createdAt >= start && createdAt <= end && d.status === 'approved';
        }).length;
        
        // Creators engaged that month
        const monthCreators = applicationsData.filter((a: ApplicationRow) => {
          const createdAt = new Date(a.created_at);
          return createdAt >= start && createdAt <= end && a.status === 'approved';
        }).length;

        // Get previous month values
        const prevIndex = i - 1;
        const prevRevenue = prevIndex >= 0 && revenueData[prevIndex] ? revenueData[prevIndex].value : undefined;
        const prevDeliverables = prevIndex >= 0 && deliverableData[prevIndex] ? deliverableData[prevIndex].value : undefined;
        const prevCreators = prevIndex >= 0 && creatorData[prevIndex] ? creatorData[prevIndex].value : undefined;

        revenueData.push({
          label: monthLabels[i],
          value: monthRevenue,
          previousValue: prevRevenue,
        });

        deliverableData.push({
          label: monthLabels[i],
          value: monthDeliverables,
          previousValue: prevDeliverables,
        });

        creatorData.push({
          label: monthLabels[i],
          value: monthCreators,
          previousValue: prevCreators,
        });
      }

      return { revenueData, deliverableData, creatorData };
    } catch (error) {
      console.error('Error fetching performance trends:', error);
      // Return empty data on error
      return {
        revenueData: [],
        deliverableData: [],
        creatorData: [],
      };
    }
  },
});

export default agencyService;
